import { createEffect, createMemo, createSignal, onCleanup, onMount, Show } from "solid-js";
import CosmologyCanvas, { type ViewportInfo } from "./components/CosmologyCanvas";
import Counters from "./components/Counters";
import PieceAuthorPanel from "./components/PieceAuthorPanel";
import ProvenanceBadge, { type ProvenanceVerdict } from "./components/ProvenanceBadge";
import ShareButton from "./components/ShareButton";
import {
  assignSlots,
  CELL_SIZE,
  MOVE_ANIM_MS,
  type Piece,
  type PieceAnim,
} from "./lib/cosmology";
import { apiBuildStamp, fetchPieceDetail, fetchPieces, type ViewportQuery } from "./lib/api";
import { mediumFor, thumbFor } from "./lib/img";
import { useLocale, type Locale } from "./i18n/LocaleProvider";
import { refreshMe, user } from "./lib/auth";
import { likedPieces, seedLiked, toggleLike } from "./lib/likes";
import {
  openTapestryStream,
  type CellChange,
  type ZoneKey,
} from "./lib/tapestry";

/** Single-line build-stamp showing web + api versions side by side, with one
 *  copy button that snapshots both into the clipboard. Font + size match the
 *  brand title so the footer reads as part of the same typographic system. */
function BuildStampBar(props: { web: { ver: string; date: string }; api: string }) {
  const apiParts = () => {
    // API header format: `v{ver}·{sha}·{date}` — split into ver + date, drop sha.
    const s = props.api;
    if (!s || s === "—") return { ver: "—", date: "" };
    const m = s.match(/^v([^·]+)·[^·]+·(.+)$/);
    if (!m) return { ver: s, date: "" };
    return { ver: m[1], date: m[2] };
  };
  const copyText = () => {
    const a = apiParts();
    return `web ${props.web.ver} · ${props.web.date}\napi ${a.ver} · ${a.date}`;
  };
  return (
    <span
      class="pointer-events-auto inline-flex items-baseline gap-3 bg-slate-950/85 backdrop-blur-sm rounded-lg px-3 py-1 shadow-lg shadow-black/30 ring-1 ring-white/5 font-display text-xs sm:text-sm tracking-tight leading-none"
      data-testid="build-stamp-bar"
    >
      <span class="inline-flex items-baseline gap-1.5" data-testid="build-stamp-web">
        <span class="text-amber-300 uppercase">web</span>
        <span class="text-slate-200">{props.web.ver}</span>
        <span class="text-slate-400">·</span>
        <span class="text-slate-300">{props.web.date}</span>
      </span>
      <span class="inline-flex items-baseline gap-1.5" data-testid="build-stamp-api">
        <span class="text-amber-300 uppercase">api</span>
        <span class="text-slate-200">{apiParts().ver}</span>
        <Show when={apiParts().date}>
          <span class="text-slate-400">·</span>
          <span class="text-slate-300">{apiParts().date}</span>
        </Show>
      </span>
      <button
        type="button"
        class="text-slate-400 hover:text-slate-100 transition cursor-pointer"
        title="Copiar web + api"
        aria-label="Copiar web + api"
        data-testid="build-stamp-copy"
        onClick={async (e) => {
          try {
            await navigator.clipboard.writeText(copyText());
          } catch {
            return;
          }
          const btn = e.currentTarget;
          const prev = btn.textContent;
          btn.textContent = "✓";
          setTimeout(() => {
            btn.textContent = prev;
          }, 1200);
        }}
      >
        ⧉
      </button>
    </span>
  );
}

/** `?highlight=<piece_id>` is forwarded from /create's save CTA. The canvas
 *  does not yet support highlighting; we read it as a graceful no-op. */
function readHighlightParam(): string | null {
  if (typeof window === "undefined") return null;
  const v = new URLSearchParams(window.location.search).get("highlight");
  return v && v.length > 0 ? v : null;
}

/** WEB37 — `/p/:id` lands here as `?focus=<id>` (PieceRoute also persists
 *  `localStorage["ikamiro:focus"]` as a backup). Param wins; storage fills
 *  the gap when the redirect drops the query string. */
const FOCUS_STORAGE_KEY = "ikamiro:focus";

function readFocusId(): string | null {
  if (typeof window === "undefined") return null;
  const q = new URLSearchParams(window.location.search).get("focus");
  if (q && q.length > 0) return q;
  try {
    const raw = window.localStorage.getItem(FOCUS_STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { piece_id?: unknown; ts?: unknown };
    if (typeof v.piece_id !== "string" || v.piece_id.length === 0) return null;
    if (typeof v.ts === "number" && Date.now() - v.ts > 10 * 60 * 1000) return null;
    return v.piece_id;
  } catch {
    return null;
  }
}

function consumeFocusId(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(FOCUS_STORAGE_KEY);
  } catch {
    // ignore
  }
  try {
    const u = new URL(window.location.href);
    if (u.searchParams.has("focus")) {
      u.searchParams.delete("focus");
      window.history.replaceState(null, "", u.toString());
    }
  } catch {
    // ignore
  }
}

export default function App() {
  const [pieces, setPieces] = createSignal<Piece[]>([]);
  const canvasPieces = createMemo(() =>
    pieces()
      .filter((p) => typeof p.x === "number" && typeof p.y === "number")
      .map((p) => ({
        id: p.id,
        imageUrl: p.imageUrl,
        thumbUrl: p.thumbUrl,
        mediumUrl: p.mediumUrl,
        x: p.x as number,
        y: p.y as number,
        likes: p.likes,
        anim: p.anim,
        wasCentre: p.wasCentre,
        isOwn: p.isOwn,
      })),
  );
  const [openPiece, setOpenPiece] = createSignal<Piece | null>(null);
  const [prevId, setPrevId] = createSignal<string | null>(null);
  const [nextId, setNextId] = createSignal<string | null>(null);
  const { locale, setLocale, t } = useLocale();
  const highlight = readHighlightParam();
  // WEB37 — deep-link pan target (cell coords resolved on mount) + the
  // missing-piece toast when the id does not resolve.
  const [focusCell, setFocusCell] = createSignal<{ x: number; y: number } | null>(null);
  const [focusMissing, setFocusMissing] = createSignal(false);
  // WEB60 — coarse pointers (phones/tablets) skip the wizard by default.
  const createHref =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches
      ? "/create/quick"
      : "/create";

  const navTo = (id: string) => {
    void fetchPieceDetail(id).then((detail) => {
      if (!detail) return;
      const existing = pieces().find((p) => p.id === id);
      const merged: Piece = existing
        ? { ...existing, ...detail.piece, slot: existing.slot, spawnedAt: existing.spawnedAt }
        : { ...(detail.piece as unknown as Piece), slot: 0, spawnedAt: 0 };
      setOpenPiece(merged);
      setPrevId(detail.prevId);
      setNextId(detail.nextId);
      if (typeof window !== "undefined") {
        const u = new URL(window.location.href);
        u.searchParams.set("highlight", id);
        window.history.replaceState(null, "", u.toString());
      }
    });
  };

  createEffect(() => {
    const open = openPiece();
    if (!open) {
      setPrevId(null);
      setNextId(null);
      return;
    }
    // Refresh rank + prev/next every time the lightbox surfaces a piece —
    // ranks can shift between clicks.
    void fetchPieceDetail(open.id).then((detail) => {
      if (!detail) return;
      setPrevId(detail.prevId);
      setNextId(detail.nextId);
      setOpenPiece((cur) =>
        cur && cur.id === open.id
          ? { ...cur, ...detail.piece, slot: cur.slot, spawnedAt: cur.spawnedAt }
          : cur,
      );
    });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenPiece(null);
      else if (e.key === "ArrowRight") {
        const id = nextId();
        if (id) navTo(id);
      } else if (e.key === "ArrowLeft") {
        const id = prevId();
        if (id) navTo(id);
      }
    };
    window.addEventListener("keydown", onKey);
    onCleanup(() => window.removeEventListener("keydown", onKey));
  });

  // Track the last viewport we fetched for; refetch when the viewport
  // settles outside the buffer ring (>r/4 cells off-centre, or r delta >20 %).
  let lastFetched: ViewportQuery | null = null;

  const onViewportChange = (info: ViewportInfo) => {
    const cx = Math.round(info.worldCx / CELL_SIZE);
    const cy = Math.round(info.worldCy / CELL_SIZE);
    const r = Math.max(8, Math.ceil((info.worldHalf / CELL_SIZE) * 1.5));
    if (lastFetched) {
      const dcx = Math.abs(cx - lastFetched.cx);
      const dcy = Math.abs(cy - lastFetched.cy);
      const dr = Math.abs(r - lastFetched.r) / Math.max(1, lastFetched.r);
      const drift = Math.max(dcx, dcy);
      if (drift <= lastFetched.r / 4 && dr <= 0.2) return;
    }
    loadWindow({ cx, cy, r });
  };

  const loadWindow = (view: ViewportQuery) => {
    void fetchPieces(view).then(({ pieces: items, window }) => {
      lastFetched = { cx: window.cx, cy: window.cy, r: window.r };
      const seeded = assignSlots(items);
      // Merge: keep any in-flight animations on pieces we already had.
      setPieces((cur) => {
        if (cur.length === 0) return seeded;
        const prev = new Map(cur.map((p) => [p.id, p] as const));
        return seeded.map((p) => {
          const old = prev.get(p.id);
          if (!old) return p;
          return { ...p, slot: old.slot, spawnedAt: old.spawnedAt };
        });
      });
      seedLiked(items.filter((it) => it.myVote === 1).map((it) => it.id));
    });
  };

  onMount(() => {
    // Cold-load: read stored view if any; otherwise (0,0,DEFAULT_R) on the
    // server. The world centre + half-side in cells is the right query.
    let initialView: ViewportQuery = { cx: 0, cy: 0, r: 25 };
    if (typeof window !== "undefined") {
      try {
        const raw = window.localStorage.getItem("ikamiro:view:v9");
        if (raw) {
          const v = JSON.parse(raw) as { panX?: number; panY?: number; zoom?: number };
          if (
            typeof v.panX === "number" &&
            typeof v.panY === "number" &&
            typeof v.zoom === "number" &&
            v.zoom > 0
          ) {
            const worldCx = -v.panX / v.zoom;
            const worldCy = -v.panY / v.zoom;
            const worldHalf =
              Math.max(window.innerWidth, window.innerHeight) / (2 * v.zoom);
            initialView = {
              cx: Math.round(worldCx / CELL_SIZE),
              cy: Math.round(worldCy / CELL_SIZE),
              r: Math.max(8, Math.ceil((worldHalf / CELL_SIZE) * 1.5)),
            };
          }
        }
      } catch {
        // ignore
      }
    }
    loadWindow(initialView);

    // WEB37 — resolve the deep-link pan target. Coordinates come from the
    // piece detail endpoint (the focused piece may be outside the loaded
    // viewport window); the canvas centres on them with zoom preserved.
    const focusId = readFocusId();
    if (focusId) {
      void fetchPieceDetail(focusId).then((detail) => {
        const x = detail?.piece?.x;
        const y = detail?.piece?.y;
        if (typeof x === "number" && typeof y === "number") {
          setFocusCell({ x, y });
        } else {
          setFocusMissing(true);
          window.setTimeout(() => setFocusMissing(false), 4500);
        }
        consumeFocusId();
      });
    }

    // v9: ONE mural, ONE owning stream ("oort"). The wire still has two
    // zones for backward compat (nucleus DO is a no-op shim) but every
    // piece — including the centre — is broadcast on the oort stream.
    // We accept tick frames regardless of the piece's stale `displayZone`.
    const zoneOf = (_p: Piece): ZoneKey => "oort";

    // v9 WS — tick frames are server-authoritative. `x`/`y` in a changes[]
    // entry comes from the DO's run_single_tick_step() and must be applied;
    // clients no longer simulate movement independently. "added" frames
    // carry image_url + (x,y) so new pieces paint immediately.
    const applyChanges = (
      cur: Piece[],
      zone: ZoneKey,
      changes: CellChange[],
    ): Piece[] => {
      if (changes.length === 0) return cur;
      const known = new Set(cur.map((p) => p.id));
      const toAdd: Piece[] = [];
      const now = typeof performance === "undefined" ? 0 : performance.now();
      let nextSlot = cur.length;
      for (const ch of changes) {
        if (known.has(ch.piece_id)) continue;
        if (typeof ch.x !== "number" || typeof ch.y !== "number") continue;
        if (!ch.image_url) continue;
        const newPiece: Piece = {
          id: ch.piece_id,
          slot: nextSlot++,
          imageUrl: ch.image_url,
          thumbUrl: thumbFor(ch.image_url),
          mediumUrl: mediumFor(ch.image_url),
          spawnedAt: now,
          likes: ch.likes ?? 0,
          dislikes: 0,
          x: ch.x,
          y: ch.y,
          displayZone: ch.x === 0 && ch.y === 0 ? "nucleus" : "oort",
        };
        // v9: oort owns every cell. Drop new pieces from the oort
        // stream regardless of derived displayZone; nucleus shim never
        // emits "added" frames so this branch covers the centre too.
        if (zone === "oort") {
          toAdd.push(newPiece);
        }
      }
      const byId = new Map<string, CellChange>();
      for (const ch of changes) byId.set(ch.piece_id, ch);
      let dirty = false;
      const next = cur.map((p) => {
        if (zoneOf(p) !== zone) return p;
        const ch = byId.get(p.id);
        if (!ch) return p;
        const newLikes = typeof ch.likes === "number" ? ch.likes : p.likes;
        const serverX = typeof ch.x === "number" ? ch.x : undefined;
        const serverY = typeof ch.y === "number" ? ch.y : undefined;
        const posChanged =
          serverX !== undefined && serverY !== undefined &&
          (serverX !== p.x || serverY !== p.y);
        if (newLikes === p.likes && !posChanged) return p;
        dirty = true;
        if (posChanged) {
          const anim: PieceAnim = {
            fromX: p.x as number,
            fromY: p.y as number,
            startMs: now,
            durationMs: MOVE_ANIM_MS,
            role: "bumper",
          };
          return { ...p, likes: newLikes, x: serverX, y: serverY, anim };
        }
        return { ...p, likes: newLikes };
      });
      if (toAdd.length === 0) return dirty ? next : cur;
      return [...(dirty ? next : cur), ...toAdd];
    };

    const closeStream = openTapestryStream((msg) => {
      if (msg.kind === "welcome") {
        // Cold-load snapshot — reconcile `likes` to the server value.
        // Positions are already trusted from `/pieces`.
        const byId = new Map(msg.pieces.map((e) => [e.piece_id, e] as const));
        setPieces((cur) =>
          cur.map((p) => {
            if (zoneOf(p) !== msg.zone) return p;
            const entry = byId.get(p.id);
            if (!entry || entry.likes === p.likes) return p;
            return { ...p, likes: entry.likes };
          }),
        );
        return;
      }
      setPieces((cur) => applyChanges(cur, msg.zone, msg.changes));
    });

    onCleanup(() => {
      closeStream();
    });
  });


  return (
    <div
      class="fixed inset-0 overflow-hidden bg-black"
      data-highlight={highlight ?? undefined}
    >
      {/* Mural canvas — fills the whole viewport behind the UI overlays. */}
      <CosmologyCanvas
        onViewportChange={onViewportChange}
        pieces={canvasPieces()}
        likedIds={likedPieces()}
        onLike={toggleLike}
        onOpen={navTo}
        focusCell={focusCell()}
        onFocusConsumed={() => setFocusCell(null)}
      />

      {/* WEB37 — unknown/decayed deep-link id lands on the default view. */}
      <Show when={focusMissing()}>
        <div
          class="absolute top-20 left-1/2 -translate-x-1/2 z-30 rounded-full bg-slate-950/85 px-4 py-2 text-xs text-amber-100 backdrop-blur-sm ring-1 ring-white/10"
          data-testid="focus-missing-toast"
        >
          {t("focusMissing")}
        </div>
      </Show>

      {/* Piece lightbox — image centered, author panel on the right.
          The panel collapses below the image on narrow viewports. */}
      <Show when={openPiece()}>
        {(p) => (
          <div
            class="absolute inset-0 z-20 flex items-center justify-center bg-slate-950/85 backdrop-blur-sm overflow-auto"
            onClick={() => setOpenPiece(null)}
            data-testid="piece-lightbox"
          >
            <div
              class="flex flex-col md:flex-row items-start gap-6 md:gap-10 max-w-[96vw] p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div class="flex flex-col items-center gap-3">
                <img
                  src={p().mediumUrl || p().imageUrl}
                  onError={(e) => {
                    const img = e.currentTarget;
                    if (img.src !== p().imageUrl) img.src = p().imageUrl;
                  }}
                  onClick={() => setOpenPiece(null)}
                  alt=""
                  class="max-w-[90vw] md:max-w-[60vw] max-h-[75vh] rounded-2xl shadow-2xl shadow-black/40 border-8 border-stone-50 cursor-pointer"
                />
                <div class="flex items-center gap-2">
                  <ProvenanceBadge verdict={p().provenanceVerdict as ProvenanceVerdict | undefined} />
                  <ShareButton
                    variant="inline"
                    url={`${window.location.origin}/?highlight=${encodeURIComponent(p().id)}`}
                    title={t("brand")}
                    text={t("sharePieceText")}
                    label={t("shareButton")}
                    ariaLabel={t("sharePieceAria")}
                    testId="share-piece"
                  />
                </div>
              </div>
              <PieceAuthorPanel
                piece={p()}
                prevId={prevId()}
                nextId={nextId()}
                onNav={navTo}
              />
            </div>
            <button
              type="button"
              aria-label={t("close")}
              onClick={(e) => {
                e.stopPropagation();
                setOpenPiece(null);
              }}
              class="absolute top-5 right-5 w-10 h-10 inline-flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition text-2xl leading-none"
            >
              ×
            </button>
          </div>
        )}
      </Show>

      {/* Top-left overlay: brand · counters · locale · tagline.
          Sits on a solid backdrop so text stays legible over any image
          panned underneath the mural. */}
      <header class="absolute top-0 left-0 z-10 p-4 sm:p-5 pointer-events-none">
        <div class="pointer-events-auto flex flex-col gap-2 bg-slate-950/85 backdrop-blur-sm rounded-2xl px-4 py-3 sm:px-5 sm:py-4 shadow-lg shadow-black/30 ring-1 ring-white/5">
          <div class="flex items-baseline flex-wrap gap-x-5 gap-y-2">
            <span class="font-display text-2xl sm:text-3xl tracking-tight text-amber-300 leading-none">
              {t("brand")}
            </span>
            <Counters
              compact
              labels={{
                pieces: t("counterPieces"),
                authors: t("counterAuthors"),
                countries: t("counterCountries"),
              }}
            />
            <div class="flex items-center gap-1 text-xs font-display" data-testid="locale-toggle">
              {(["en", "es"] as const).map((l: Locale) => (
                <button
                  type="button"
                  onClick={() => setLocale(l)}
                  aria-pressed={locale() === l}
                  data-locale={l}
                  class={`px-2 py-0.5 rounded transition ${
                    locale() === l
                      ? "bg-amber-500/20 text-amber-200"
                      : "text-slate-300 hover:text-white hover:bg-slate-800/60"
                  }`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <AuthNav />
          </div>
          <p class="text-[11px] sm:text-xs uppercase tracking-widest text-slate-300 max-w-[640px]">
            {t("tagline")}
          </p>
          <button
            type="button"
            onClick={() =>
              window.dispatchEvent(new CustomEvent("ikamiro:reset-view"))
            }
            class="self-start text-[10px] sm:text-[11px] uppercase tracking-widest text-slate-300 hover:text-amber-200 underline decoration-dotted underline-offset-4 transition"
            data-testid="reset-view"
          >
            {t("resetView")}
          </button>
        </div>
      </header>

      {/* Bottom-right floating CTAs: share project + create. */}
      <div class="absolute bottom-6 right-6 z-10 flex items-center gap-3">
        <ShareButton
          variant="fab"
          url={typeof window !== "undefined" ? window.location.origin + "/" : "https://ikamiro.com/"}
          title={t("brand")}
          text={t("shareProjectText")}
          ariaLabel={t("shareProjectAria")}
          testId="share-project"
        />
        <a
          href={createHref}
          class="inline-flex items-center justify-center bg-amber-500 text-slate-950 text-base font-medium px-6 py-3 rounded-full shadow-lg shadow-amber-500/20 hover:bg-amber-400 transition"
          data-testid="home-create-cta"
        >
          {t("homeCreateCta")}
        </a>
      </div>

      {/* Bottom-left footer: dual build-stamp (web + API).
          API stamp travels in `x-api-build` response header (captured by
          fetchPieces) so the operator can verify both sides are aligned. */}
      <footer class="absolute bottom-4 left-4 sm:bottom-5 sm:left-5 z-10 pointer-events-none hidden min-[420px]:block">
        <BuildStampBar
          web={{ ver: __APP_VERSION__, date: __APP_BUILD_DATE__ }}
          api={apiBuildStamp() || "—"}
        />
      </footer>
    </div>
  );
}

/** Identity chip — round, icon-only. Anonymous: silhouette glyph linking
 *  to /signin. Signed in: first letter of the display name on an amber
 *  disc, linking to /me. Lives in the header next to the locale toggle so
 *  it never crowds the canvas surface. */
function AuthNav() {
  const { t } = useLocale();
  onMount(() => {
    void refreshMe();
  });
  const initial = () => {
    const u = user();
    if (!u) return "";
    const name = (u.display_name ?? u.username ?? "").trim();
    return (name[0] ?? "?").toUpperCase();
  };
  return (
    <Show
      when={user()}
      fallback={
        <a
          href="/signin"
          aria-label={t("nav.signin")}
          title={t("nav.signin")}
          data-testid="nav-signin"
          class="inline-flex items-center justify-center w-7 h-7 rounded-full border border-slate-700 bg-slate-900/60 text-slate-300 hover:text-amber-200 hover:border-amber-500/60 transition"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" class="w-4 h-4">
            <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-3.31 0-8 1.67-8 5v1h16v-1c0-3.33-4.69-5-8-5Z" />
          </svg>
        </a>
      }
    >
      {(u) => (
        <a
          href="/me"
          aria-label={t("nav.my_pieces")}
          title={u().display_name ?? u().username ?? t("nav.my_pieces")}
          data-testid="nav-my-pieces"
          class="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-500 text-slate-950 font-display text-xs hover:bg-amber-400 transition"
        >
          {initial()}
        </a>
      )}
    </Show>
  );
}
